import React, { useState } from "react"
import { IRecord } from "../../types/schema"
import { Fields } from "@cd2/forms"
import { useAsyncEffect } from "../../hooks/useAsyncEffect"
import { useGlobalState } from "../GlobalStateProvider"
import { IBranchSettings } from "../../utils/useSettings"
import { dublCorkBranchId } from "../../../shared/constants"

type IBranch = IRecord<"branches", "name"> & {
  currency_name?: string
  is_fortus_ew_branch?: boolean
  alt_id?: string
  settings?: IBranchSettings
}

interface IProps {
  onChange: (branch: IBranch) => void
  value?: IBranch | string
  label?: React.ReactNode
  disabled?: boolean
  skeemFilter?: Record<string, any>
  isClearable?: boolean
  error?: string
  onlyAccessibleBranches?: boolean
  onlyBulkEmailBranches?: boolean
  customFilter?: (branch: IBranch) => boolean
  splitCorkDublin?: boolean
  required?: boolean
  menuPortalTarget?: HTMLElement | null
  getOptionLabel?: (branch: IBranch) => string
  /** Label options with the branch's display name (settings.branchName) instead of its internal name */
  useDisplayName?: boolean
}

export function BranchSelect({
  onChange,
  value,
  label = "Branches",
  disabled,
  skeemFilter,
  isClearable,
  error,
  onlyAccessibleBranches,
  onlyBulkEmailBranches,
  customFilter,
  splitCorkDublin,
  required,
  menuPortalTarget,
  getOptionLabel,
  useDisplayName,
}: IProps) {
  const skeem = window.skeem
  const { currentUser } = useGlobalState()
  const [branches, setBranches] = useState<IBranch[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useAsyncEffect(async () => {
    await fetchBranches()
  }, [JSON.stringify(skeemFilter)])

  const filter: any = {
    and: [
      { not: { matches: { name: { like: "test" } } } },
      { not: { matches: { name: { like: "demo" } } } },
      { not: { matches: { name: { like: "training" } } } },
    ].filter(Boolean),
  }

  async function fetchBranches() {
    if (skeemFilter) {
      filter.and.push(skeemFilter as any)
    }
    if (onlyAccessibleBranches) {
      if (currentUser.accessibleBranches.length > 0) {
        filter.and.push({
          matches: { id: currentUser.accessibleBranches.map(branch => branch.id) },
        })
      } else {
        filter.and.push({
          matches: { id: currentUser.branch.id },
        })
      }
    }
    if (onlyBulkEmailBranches && currentUser.accessibleBulkEmailBranches.length > 0) {
      filter.and.push({
        matches: {
          id: {
            or: [
              currentUser.branch.id,
              ...currentUser.accessibleBulkEmailBranches.map(branch => branch.id),
            ],
          },
        },
      })
    } else if (onlyBulkEmailBranches) {
      filter.and.push({
        matches: { id: currentUser.branch.id },
      })
    }
    setIsLoading(true)
    try {
      let branches = await skeem.fetch2<IBranch[]>({
        model: {
          name: "branches",
          attributes: [
            "name",
            "currency_name",
            "is_fortus_ew_branch",
            (customFilter || useDisplayName) && "settings",
          ].filter(Boolean),
          sort: { by: "name", direction: "asc" },
          filter,
        },
      })
      if (splitCorkDublin) {
        const withoutCD = branches.filter(b => b.id !== dublCorkBranchId)
        const cd = branches.find(b => b.id === dublCorkBranchId)
        if (cd) {
          branches = [
            ...withoutCD,
            {
              name: "Dublin",
              id: cd.id,
              alt_id: dublCorkBranchId + "_dublin",
              currency_name: cd.currency_name,
              is_fortus_ew_branch: cd.is_fortus_ew_branch,
            },
            {
              name: "Cork",
              id: dublCorkBranchId,
              alt_id: dublCorkBranchId + "_cork",
              currency_name: cd.currency_name,
              is_fortus_ew_branch: cd.is_fortus_ew_branch,
            },
          ]
        }
      }
      if (customFilter) {
        setBranches(branches.filter(customFilter))
      } else {
        setBranches(branches)
      }
    } finally {
      setIsLoading(false)
    }
  }

  function handleChange(val: IBranch) {
    onChange(val)
  }

  function branchLabel(branch: IBranch) {
    if (getOptionLabel) {
      return getOptionLabel(branch)
    }
    if (useDisplayName) {
      // the selected value may be passed in without its settings, so fall back to the loaded record
      const loaded = branch.settings ? branch : branches.find(b => b.id === branch.id)
      return loaded?.settings?.branchName || branch.name
    }
    return branch.name
  }

  const sortedBranches = [...branches].sort((a, b) => branchLabel(a).localeCompare(branchLabel(b)))

  return (
    <Fields.Select2
      label={label}
      getOptionLabel={branchLabel}
      getOptionValue={opt => opt.alt_id ?? opt.id}
      options={sortedBranches}
      isLoading={isLoading}
      onChange={handleChange}
      value={
        typeof value === "string"
          ? branches.find(branch => branch.alt_id == value || branch.id === value)
          : value
          ? {
              // a value passed in with just an id has no name, so use the loaded record
              ...(value.name ? value : branches.find(b => b.id === value.id) ?? value),
              currency_name: value.currency_name || "",
            }
          : undefined
      }
      disabled={disabled}
      isClearable={isClearable}
      error={error}
      showRequiredStar={required}
      menuPortalTarget={menuPortalTarget}
    />
  )
}
